/**
 * LIVE WEBSITE SAMPLE — Oak & Iron Distilling Co.
 * A fictional artisan bourbon distillery. Shown to prospects as
 * a proof-of-quality example of the $1,499 Website tier.
 *
 * Standalone: no SiteNav, no cross-nav — this is a client sample,
 * not part of the Resilience shell.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function SampleWebsite() {
  useEffect(() => {
    document.title = "Oak & Iron Distilling Co. — Cask Strength, Missouri.";
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0a06] text-[#f5eddc]" data-testid="sample-website">
      {/* Sample banner */}
      <div className="w-full bg-[#1a120a] border-b border-[#d4a442]/30 text-center py-2 text-[11px] tracking-[0.3em] uppercase text-[#d4a442]">
        Website Sample &nbsp;·&nbsp;
        <Link to="/products/website" className="underline hover:text-[#f8c261]" data-testid="sample-website-back">
          Back to Resilience Studios
        </Link>
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-20 bg-[#0d0a06]/90 backdrop-blur-md border-b border-[#d4a442]/15">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="font-serif text-xl tracking-widest text-[#d4a442]">OAK &amp; IRON</div>
          <nav className="hidden md:flex gap-8 text-sm tracking-widest uppercase text-[#c9b98a]">
            <a href="#story" className="hover:text-[#f8c261]">The Distillery</a>
            <a href="#bottles" className="hover:text-[#f8c261]">The Bottles</a>
            <a href="#press" className="hover:text-[#f8c261]">The Press</a>
            <a href="#visit" className="hover:text-[#f8c261]">Visit</a>
          </nav>
          <a
            href="#bottles"
            className="text-xs tracking-[0.3em] uppercase border border-[#d4a442] text-[#d4a442] px-4 py-2 hover:bg-[#d4a442] hover:text-[#0d0a06] transition"
            data-testid="sample-website-order-cta"
          >
            Reserve a Bottle
          </a>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative min-h-[92vh] flex items-end overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(13,10,6,0) 30%, rgba(13,10,6,0.95) 100%), url(/samples/website/oak_iron_hero.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 pb-20 relative z-10">
          <div className="text-xs tracking-[0.4em] uppercase text-[#d4a442] mb-6">
            Est. 2019 · Franklin County, Missouri
          </div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-3xl">
            The bourbon that doesn't apologise for the summers it slept through.
          </h1>
          <p className="mt-6 max-w-xl text-lg text-[#c9b98a] leading-relaxed">
            Six years in charred oak. One warehouse without air conditioning.
            Every barrel is dated in the founder's handwriting, and every bottle
            was tasted by the person whose signature is on the label.
          </p>
          <div className="mt-10 flex gap-4">
            <a
              href="#bottles"
              className="px-8 py-4 bg-[#d4a442] text-[#0d0a06] font-medium tracking-widest uppercase text-xs hover:bg-[#f8c261] transition"
            >
              See the Bottles
            </a>
            <a
              href="#visit"
              className="px-8 py-4 border border-[#d4a442] text-[#d4a442] font-medium tracking-widest uppercase text-xs hover:bg-[#d4a442]/10 transition"
            >
              Book a Tasting
            </a>
          </div>
        </div>
      </section>

      {/* Story */}
      <section id="story" className="max-w-4xl mx-auto px-6 py-32">
        <div className="text-xs tracking-[0.4em] uppercase text-[#d4a442] mb-6">The Distillery</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-10">
          We inherited a barn. Nobody told us it was a distillery.
        </h2>
        <div className="space-y-6 text-lg text-[#c9b98a] leading-loose">
          <p>
            My grandfather bought the property in 1953 to store hay. He also, without ever mentioning
            it to my grandmother, spent the next thirty-one years quietly filling the loft with sixty
            oak barrels that he did not open, did not sell, and did not tell anyone about.
          </p>
          <p>
            We found them in the summer of 2018 while cleaning out the barn to sell the land. Fifty-nine
            of the sixty barrels were still full. Two of us tasted the first one on the barn floor in
            the July heat and stood there in silence for what my brother later swore was a full four
            minutes. Then we cancelled the sale.
          </p>
          <p>
            We do not have my grandfather's mash bill. We do not have his still. We do not have any
            record of how he did it. What we do have is his patience — his willingness to leave a
            barrel alone through fifteen Missouri summers without opening it once — and that is the
            only thing we have tried to copy.
          </p>
          <p>
            Every bottle we ship carries a date in the founder's handwriting. That is the day the
            barrel was tasted and approved. If the handwriting looks a little tired, it is because
            the barrel took a little longer.
          </p>
        </div>
        <div className="mt-14 font-serif italic text-[#d4a442] text-xl">
          — Elias Marren, Founder &amp; Master Distiller
        </div>
      </section>

      {/* Bottles */}
      <section id="bottles" className="bg-[#12100a] border-y border-[#d4a442]/15 py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs tracking-[0.4em] uppercase text-[#d4a442] mb-4 text-center">The Bottles</div>
          <h2 className="font-serif text-4xl md:text-5xl text-center mb-4">Three that made it out.</h2>
          <p className="text-center text-[#c9b98a] max-w-xl mx-auto mb-16">
            We release under two hundred cases a year. Each barrel is dumped by hand, cut with the
            same limestone water it was rested on, and bottled the day it is approved.
          </p>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                name: "Summer of '19",
                proof: "121.4",
                age: "6 Years",
                notes: "Charred pecan, black cherry, tobacco leaf. A long, slow, unapologetic finish.",
                price: "$118",
              },
              {
                name: "Silent Barn",
                proof: "108.2",
                age: "8 Years",
                notes: "Vanilla bean, orange peel, dark honey. Reserved. Drinks like it is thinking.",
                price: "$164",
              },
              {
                name: "Marren Reserve",
                proof: "134.6",
                age: "11 Years",
                notes: "Leather, coffee cherry, dried fig. Sold only to those who have visited us in person.",
                price: "$298",
              },
            ].map((b) => (
              <div key={b.name} className="border border-[#d4a442]/25 bg-[#0d0a06] p-8 hover:border-[#d4a442] transition">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#d4a442] mb-4">
                  Cask Strength · {b.age} · {b.proof} Proof
                </div>
                <h3 className="font-serif text-3xl mb-4">{b.name}</h3>
                <p className="text-[#c9b98a] leading-relaxed mb-6 text-sm">{b.notes}</p>
                <div className="flex items-center justify-between border-t border-[#d4a442]/20 pt-4">
                  <div className="text-xl font-serif text-[#d4a442]">{b.price}</div>
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    className="text-xs tracking-widest uppercase text-[#c9b98a] hover:text-[#f8c261]"
                  >
                    Reserve →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Press */}
      <section id="press" className="max-w-5xl mx-auto px-6 py-32">
        <div className="text-xs tracking-[0.4em] uppercase text-[#d4a442] mb-10 text-center">The Press</div>
        <div className="grid md:grid-cols-3 gap-10">
          {[
            {
              q: "The most patient bourbon I have tasted this year. And I have tasted every bourbon this year.",
              a: "Whisky Advocate",
            },
            {
              q: "A distillery that refuses to explain itself, and doesn't have to.",
              a: "The Bourbon Review",
            },
            {
              q: "Elias Marren is either a genius or a very lucky farmer. The bottle in front of me is not letting me decide.",
              a: "GQ",
            },
          ].map((p) => (
            <blockquote key={p.a} className="border-l-2 border-[#d4a442] pl-6">
              <p className="font-serif italic text-lg text-[#f5eddc] leading-relaxed mb-4">"{p.q}"</p>
              <div className="text-xs tracking-widest uppercase text-[#d4a442]">— {p.a}</div>
            </blockquote>
          ))}
        </div>
      </section>

      {/* Visit */}
      <section id="visit" className="bg-[#12100a] border-t border-[#d4a442]/15 py-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs tracking-[0.4em] uppercase text-[#d4a442] mb-6">Visit</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-8">
            We are one hour west of St. Louis. We do not have a gift shop.
          </h2>
          <p className="text-lg text-[#c9b98a] leading-relaxed mb-10">
            Tastings are by appointment only, six people per session, one session per Saturday.
            You will taste four expressions, meet the barrel that sat under the hottest corner
            of the barn last August, and leave with a bottle we chose for you.
          </p>
          <form className="max-w-md mx-auto space-y-4" onSubmit={(e) => e.preventDefault()}>
            <input
              type="email"
              placeholder="Your email"
              className="w-full bg-transparent border border-[#d4a442]/40 px-4 py-3 text-[#f5eddc] placeholder-[#a3906a] focus:border-[#d4a442] outline-none"
              data-testid="sample-website-visit-email"
            />
            <button
              type="submit"
              className="w-full bg-[#d4a442] text-[#0d0a06] py-4 tracking-widest uppercase text-xs font-medium hover:bg-[#f8c261] transition"
              data-testid="sample-website-visit-submit"
            >
              Request a Saturday
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#d4a442]/15 py-16">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8 text-sm text-[#a3906a]">
          <div className="font-serif tracking-widest text-[#d4a442]">OAK &amp; IRON DISTILLING CO.</div>
          <div>Franklin County, Missouri · Est. 2019 · Please drink patiently.</div>
        </div>
        <div className="text-center text-[11px] text-[#a3906a] mt-8">
          <span className="opacity-60">Sample website by </span>
          <Link to="/products/website" className="underline hover:text-[#d4a442]">
            Resilience Multimedia Studios
          </Link>
        </div>
      </footer>
    </div>
  );
}
